import { computed, inject } from '@angular/core';
import { withDevtools } from '@angular-architects/ngrx-toolkit';

import { patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { BehaviorSubject, filter, pipe, switchMap, tap } from 'rxjs';
import { removeAllEntities, setAllEntities, withEntities } from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';

import { ApplicationPersonsStore } from '@kros-sk/application-persons';

import { ApplicationModule } from './application-modules.types';
import { ApplicationModulesApiService } from './application-modules-api.service';

interface ApplicationModulesState {
  error: unknown | null;
}

const initialState = Object.freeze<ApplicationModulesState>({
  error: null,
});

export const ApplicationModulesStore = signalStore(
  withDevtools('Application Modules'),

  withState(initialState),

  withEntities<ApplicationModule>(),

  withComputed((store) => ({
    homeModule: computed(() => store.entities().find((m) => m.isHome)),
  })),

  withProps(() => ({
    _apiService: inject(ApplicationModulesApiService),
    _personsStore: inject(ApplicationPersonsStore),
  })),

  withProps(() => ({
    isLoading$: new BehaviorSubject(true),
  })),

  withMethods((store) => {
    return {
      load: rxMethod<string | null>(
        pipe(
          filter((personId): personId is string => personId !== null),
          tap(() => store.isLoading$.next(true)),
          switchMap((personId) =>
            store._apiService.get(personId).pipe(
              tapResponse({
                next: (modules) => patchState(store, setAllEntities(modules)),
                error: (error) => patchState(store, { error }, removeAllEntities()),
                finalize: () => store.isLoading$.next(false),
              })
            )
          )
        )
      ),
    };
  }),

  withHooks((store) => {
    return {
      onInit: (): void => {
        store.load(store._personsStore.currentPersonId);
      },
    };
  })
);
